import {
  CheckoutPlan,
  CheckoutPlansResult,
  PlanDiscountType,
  PlanIntervalUnit,
  PlanPricing,
} from "lib/types/subscriptions";

const INTERVAL_UNITS: PlanIntervalUnit[] = ["WEEK", "MONTH"];
const DISCOUNT_TYPES: PlanDiscountType[] = ["NONE", "FLAT", "PERCENTAGE"];

const readNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const readIntervalUnit = (value: unknown): PlanIntervalUnit | null => {
  const candidate = typeof value === "string" ? (value.toUpperCase() as PlanIntervalUnit) : null;
  return candidate && INTERVAL_UNITS.includes(candidate) ? candidate : null;
};

const readDiscountType = (value: unknown): PlanDiscountType => {
  const candidate = typeof value === "string" ? (value.toUpperCase() as PlanDiscountType) : null;
  return candidate && DISCOUNT_TYPES.includes(candidate) ? candidate : "NONE";
};

const readDateString = (value: unknown): string | undefined => {
  if (typeof value === "string" && value !== "") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
};

const parsePricing = (raw: any): PlanPricing => ({
  total: readNumber(raw?.total),
  originalTotal: readNumber(raw?.sub_total),
  savings: readNumber(raw?.discount),
  deliveryCount: readNumber(raw?.delivery_count),
  nextBillingDate: readDateString(raw?.next_billing_date),
  endDate: readDateString(raw?.end_date),
});

const parsePlan = (
  raw: any,
  index: number,
  subscriptionId: string,
  subscriptionName: string,
): CheckoutPlan | null => {
  if (!raw || typeof raw !== "object") return null;

  const id = typeof raw.id === "string" && raw.id ? raw.id : null;
  if (!id) return null;

  const intervalCount = readNumber(raw.interval_count);
  const intervalUnit = readIntervalUnit(raw.interval_unit);
  if (!intervalCount || intervalCount < 1 || !intervalUnit) return null;

  const maxCycles = readNumber(raw.plan_duration);

  return {
    id,
    subscriptionId,
    subscriptionName,
    intervalCount,
    intervalUnit,
    maxCycles: maxCycles && maxCycles > 0 ? maxCycles : null,
    discountType: readDiscountType(raw.discount_type),
    discountValue: readNumber(raw.discount_value) ?? 0,
    isRecommended: raw.recommended === true,
    rank: readNumber(raw.rank) ?? index,
    isSelected: false,
    pricing: parsePricing(raw),
  };
};

export const EMPTY_PLANS_RESULT: CheckoutPlansResult = {
  subscriptionId: null,
  plans: [],
  selectedPlanId: null,
};

export const parseCheckoutPlansResponse = (response: any): CheckoutPlansResult => {
  if (response?.subscribable === false) return EMPTY_PLANS_RESULT;

  const programs = Array.isArray(response?.subscriptions) ? response.subscriptions : null;
  if (!programs?.length) return EMPTY_PLANS_RESULT;

  const plans = programs
    .flatMap((program: any) => {
      const subscriptionId = typeof program?.subscription_id === "string" ? program.subscription_id : null;
      if (!subscriptionId) return [];

      const subscriptionName = typeof program?.name === "string" ? program.name : "";
      const rawPlans = Array.isArray(program?.plans) ? program.plans : [];

      return rawPlans.map((raw: any, index: number) =>
        parsePlan(raw, index, subscriptionId, subscriptionName),
      );
    })
    .filter((plan: CheckoutPlan | null): plan is CheckoutPlan => plan !== null)
    .sort((a: CheckoutPlan, b: CheckoutPlan) => a.rank - b.rank);

  if (!plans.length) return EMPTY_PLANS_RESULT;

  return {
    subscriptionId:
      plans.find((plan: CheckoutPlan) => plan.isRecommended)?.subscriptionId ?? plans[0].subscriptionId,
    plans,
    selectedPlanId: null,
  };
};

export interface AppliedSubscription {
  subscriptionId: string;
  planId: string;
}

export const readAppliedSubscription = (checkoutResponse: any): AppliedSubscription | null => {
  const raw = checkoutResponse?.metadata?.subscription;
  const subscriptionId = typeof raw?.subscription_id === "string" ? raw.subscription_id : null;
  const planId = typeof raw?.plan_id === "string" ? raw.plan_id : null;

  return subscriptionId && planId ? { subscriptionId, planId } : null;
};
